import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ensureSession, recordEvent } from "@/lib/analytics";
import { recordInAppPath } from "@/lib/routeHistory";

/**
 * Mounts once near the router. On every route change:
 *  1) Ensures an `analytics_sessions` row exists / is refreshed.
 *  2) Records a `page_view` event into `analytics_events`.
 *
 * Failures are swallowed inside the analytics layer so this component is
 * always safe to mount and never blocks navigation.
 *
 * Performance: the FIRST page_view is deferred until the browser is idle
 * (or 2.5s, or first user interaction) so the two Supabase POSTs don't
 * compete with LCP / hydration on mobile. Subsequent route changes fire
 * immediately because by then we're well past LCP.
 */
export default function AnalyticsTracker() {
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);
  const firstViewSentRef = useRef(false);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;

    // Track in-app pathname history so we can use it as a referrer_fallback
    // when document.referrer is empty (direct visits, same-origin SPA nav).
    recordInAppPath(location.pathname);

    const fire = () => {
      void ensureSession();
      void recordEvent("page_view", {
        metadata: {
          path: location.pathname,
          search: location.search,
          title: typeof document !== "undefined" ? document.title : undefined,
        },
      });
    };

    if (firstViewSentRef.current) {
      // Subsequent SPA route changes — fire immediately, we're past LCP.
      fire();
      return;
    }
    firstViewSentRef.current = true;

    // First page view — defer off the LCP critical path.
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      fire();
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(run, 1500);
    }
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll", "touchstart"];
    const onInteract = () => run();
    events.forEach((e) =>
      window.addEventListener(e, onInteract, { once: true, passive: true, capture: true }),
    );
    return () => {
      if (idleId !== undefined && typeof w.cancelIdleCallback === "function") {
        w.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      events.forEach((e) => window.removeEventListener(e, onInteract, { capture: true } as EventListenerOptions));
    };
  }, [location.pathname, location.search]);

  return null;
}
